"use client"

import { useState, useRef, useEffect, useMemo, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { MapPin, Sprout, Ruler, CalendarClock, Layers, Wrench, X } from "lucide-react"
import { ZipCodeInput } from "@/components/ui/zip-code-input"
import { analytics, trackEvent, stashEmailCaptureAttribution } from "@/lib/analytics"
import {
  HeadlineHero,
  LawnPrefillBanner,
  RelatedTools,
  ResultSuccessCard,
  ResultEmailCaptureCard,
  ToolPageLoadingFallback,
  ToolPageShell,
} from "@/components/tools/shared"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { useDefaultLawn } from "@/hooks/use-default-lawn"
import { useFromTool } from "@/hooks/use-from-tool"
import { buildToolUrl, buildRelatedToolUrl } from "@/lib/result-tool-urls"
import { formatDateRange } from "@/lib/date-utils"
import { grassTypeToGuideSlug } from "@/lib/grass-slug"
import { resolveGrassDisplayName } from "@/data/grass-characteristics"
import {
  calculateDethatching,
  type DethatchingResult,
  type ThatchDepthBand,
} from "@/lib/dethatching-calculator"
import { ThatchBandSelector } from "./components/thatch-band-selector"
import { VerdictCrossSection } from "./components/verdict-cross-section"
import { MonthWindowStrip } from "./components/month-window-strip"
import { MethodTiles } from "./components/method-tiles"

// Only grasses with data in GRASS_CHARACTERISTICS. Cool first, then warm, since
// the fall-timing cool-season audience is the larger search cohort.
const GRASS_OPTIONS: string[] = [
  "Tall Fescue",
  "Kentucky Bluegrass",
  "Perennial Ryegrass",
  "Fine Fescue",
  "Bermudagrass",
  "Zoysiagrass",
  "St. Augustinegrass",
  "Centipede",
  "Bahiagrass",
  "Buffalograss",
]

// Sample-lawn defaults (approved product decision): the page lands with a
// worked example so the verdict, cross-section, and month strip are visible
// immediately. Real prefill sources (URL params, saved lawn, saved ZIP) win
// over the sample; the region chip carries a "Sample lawn" prefix until the
// user replaces the sample ZIP.
const SAMPLE_ZIP = "43004"
const SAMPLE_GRASS = "Tall Fescue"
const SAMPLE_BAND: ThatchDepthBand = "half-to-one"
const SAMPLE_SIZE = "5500"

const VALID_BANDS: ThatchDepthBand[] = ["under-quarter", "quarter-to-half", "half-to-one", "over-one"]

type Field = "grass" | "zip" | "thatch" | "size"

function slugKey(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "")
}

/**
 * Resolve an incoming grass value - a display name, a grass key, or a guide
 * slug from a shared link (buildToolUrl slugifies to e.g. "tall-fescue") - to
 * one of the controlled <select> options, so the field never renders blank
 * while the engine still calculates a result.
 */
function resolveGrassOption(raw: string | null | undefined): string {
  if (!raw) return ""
  const key = slugKey(raw)
  const byName = GRASS_OPTIONS.find((o) => slugKey(o) === key)
  if (byName) return byName
  const bySlug = GRASS_OPTIONS.find((o) => {
    const s = grassTypeToGuideSlug(o)
    return s ? slugKey(s) === key : false
  })
  if (bySlug) return bySlug
  const canonKey = slugKey(resolveGrassDisplayName(raw.trim()))
  return GRASS_OPTIONS.find((o) => slugKey(o) === canonKey) ?? ""
}

const VERDICT_TONE: Record<
  DethatchingResult["verdict"],
  { badge: string; border: string; eyebrow: string }
> = {
  monitor: { badge: "bg-green-100 text-green-800", border: "border-green-200", eyebrow: "Good news" },
  dethatch: { badge: "bg-amber-100 text-amber-800", border: "border-amber-200", eyebrow: "Time to dethatch" },
  "dethatch-urgent": { badge: "bg-red-100 text-red-800", border: "border-red-200", eyebrow: "Heavy thatch" },
}

function isValidZip(z: string): boolean {
  return /^\d{5}$/.test(z.trim())
}

function fmtShort(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function StepChip({ n, dark }: { n: number; dark?: boolean }) {
  return (
    <span
      className={`inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-xs font-extrabold text-white ${dark ? "bg-gray-900" : "bg-green-600"}`}
    >
      {n}
    </span>
  )
}

function DethatchingContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("dethatching-calculator")
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill, settled: lawnSettled } =
    useDefaultLawn()
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()

  // Client-mount gate. The engine result derives windows and countdowns from
  // `new Date()`, and this island DOES server-render when a shared link arrives
  // with complete params. A prod server (UTC) and the visitor's browser (local
  // TZ) can land on different calendar days, so an SSR-computed result would
  // hydrate with mismatched dates. Gating the result on mount keeps the
  // time-dependent output client-only, matching the sample path (seeded in an
  // effect) which never server-renders anyway.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const [zip, setZip] = useState(searchParams.get("zip") ?? "")
  const [grassType, setGrassType] = useState(() => resolveGrassOption(searchParams.get("grass")))
  const [thatchDepth, setThatchDepth] = useState<ThatchDepthBand | "">(() => {
    const p = searchParams.get("thatch")
    return p && (VALID_BANDS as string[]).includes(p) ? (p as ThatchDepthBand) : ""
  })
  const [lawnSize, setLawnSize] = useState(searchParams.get("size") ?? "")

  // Which required/optional fields the user (or a real source) owns. Seeded
  // sample values are NOT "touched"; a field becomes touched on user edit.
  const [touched, setTouched] = useState<Record<Field, boolean>>({
    grass: false, zip: false, thatch: false, size: false,
  })
  const touchedRef = useRef(touched)
  useEffect(() => { touchedRef.current = touched }, [touched])
  const markTouched = (f: Field) =>
    setTouched((prev) => (prev[f] ? prev : { ...prev, [f]: true }))

  // True once the illustrative sample set was seeded (no real input existed).
  const [usingSample, setUsingSample] = useState(false)
  // True only when the sample's 5,500 sq ft was actually assigned (size was
  // empty at seed time). A real URL/saved-lawn size that happens to coexist
  // with a seeded sample is NOT synthetic and must not be dropped later.
  const [sampleSizeSeeded, setSampleSizeSeeded] = useState(false)

  // Brief opacity dip on the verdict block whenever an input changes (the
  // design's "poke" reveal - labor perception without fake loading).
  const [revealing, setRevealing] = useState(false)
  const revealTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const trackedUsageRef = useRef(false)
  const trackedSignaturesRef = useRef<Set<string>>(new Set())

  // Fields provided by the URL are real and must never be sample-overwritten.
  const urlProvidedRef = useRef({
    zip: !!searchParams.get("zip"),
    grass: !!resolveGrassOption(searchParams.get("grass")),
    thatch: VALID_BANDS.includes((searchParams.get("thatch") ?? "") as ThatchDepthBand),
    size: !!searchParams.get("size"),
  })

  const poke = () => {
    if (revealTimer.current) clearTimeout(revealTimer.current)
    setRevealing(true)
    revealTimer.current = setTimeout(() => setRevealing(false), 380)
  }
  useEffect(() => () => {
    if (revealTimer.current) clearTimeout(revealTimer.current)
  }, [])

  // One-time initialize, gated on the saved-lawn fetch settling. Authenticated
  // users load asynchronously, so committing the sample before the lawn
  // arrives would ignore a real saved lawn (and the banner would then claim a
  // lawn is applied that the fields don't reflect). Anonymous users never load,
  // so this resolves on mount.
  const initedRef = useRef(false)
  useEffect(() => {
    // Gate on the saved-lawn resolution settling, NOT on isLoading: isLoading
    // starts false, so it cannot distinguish "not fetched yet" from "no lawns".
    if (initedRef.current || !lawnSettled) return
    initedRef.current = true

    let z = zip, g = grassType, t = thatchDepth, s = lawnSize
    if (!z && lawnPrefill.zipCode) z = lawnPrefill.zipCode
    if (!g && lawnPrefill.grassType) g = resolveGrassOption(lawnPrefill.grassType)
    if (!s && lawnPrefill.lawnSizeSqft) s = String(lawnPrefill.lawnSizeSqft)
    if (!z) {
      // Cross-tool saved ZIP (anon). Read synchronously - useSavedInputs
      // hydrates in its own effect and would race this one.
      try {
        const stored = localStorage.getItem("lawn-care-saved-inputs")
        const sv = stored ? (JSON.parse(stored)?.zip as string | undefined) : undefined
        if (sv && /^\d{5}$/.test(sv)) z = sv
      } catch { /* ignore */ }
    }

    // The sample is all-or-nothing: seed the full example ONLY when none of the
    // three required fields has a real value. A real value in any of them
    // suppresses the sample - we never fabricate the remaining fields and pass
    // them off as the user's own (that would leak into analytics and outbound
    // links as a "real" result).
    if (!z && !g && !t) {
      z = SAMPLE_ZIP
      g = SAMPLE_GRASS
      t = SAMPLE_BAND
      // Only the sample-assigned size is synthetic; a real URL/lawn size that
      // reached `s` above stays real.
      if (!s) {
        s = SAMPLE_SIZE
        setSampleSizeSeeded(true)
      }
      setUsingSample(true)
    }

    if (z !== zip) setZip(z)
    if (g !== grassType) setGrassType(g)
    if (t !== thatchDepth) setThatchDepth(t)
    if (s !== lawnSize) setLawnSize(s)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lawnSettled, lawnPrefill.zipCode, lawnPrefill.grassType, lawnPrefill.lawnSizeSqft])

  // React to a lawn selected AFTER init (LawnPrefillBanner switch) or a late
  // authed fetch: apply it to fields the user has not edited and the URL did
  // not set. Never runs in sample mode.
  useEffect(() => {
    if (!initedRef.current || usingSample) return
    if (lawnPrefill.zipCode && !urlProvidedRef.current.zip && !touchedRef.current.zip) {
      setZip(lawnPrefill.zipCode)
    }
    if (lawnPrefill.grassType && !urlProvidedRef.current.grass && !touchedRef.current.grass) {
      const m = resolveGrassOption(lawnPrefill.grassType)
      if (m) setGrassType(m)
    }
    if (lawnPrefill.lawnSizeSqft && !urlProvidedRef.current.size && !touchedRef.current.size) {
      setLawnSize(String(lawnPrefill.lawnSizeSqft))
    }
  }, [lawnPrefill.zipCode, lawnPrefill.grassType, lawnPrefill.lawnSizeSqft, usingSample])

  // Provenance: a seeded sample stays "sample" until the user has genuinely
  // filled all three required fields themselves. Editing only the ZIP (say)
  // leaves the synthetic grass and band in place, so the result is still an
  // example and must not leak outward.
  const requiredAllReal = touched.grass && touched.zip && touched.thatch
  const isSampleResult = usingSample && !requiredAllReal

  // The only synthetic size is the sample's 5,500 sq ft that we actually
  // seeded and the user never replaced. A real URL / saved-lawn size is never
  // synthetic even when it coexists with a seeded sample, so it is kept.
  const sizeIsSyntheticLeftover = sampleSizeSeeded && !touched.size
  const sizeForEngine =
    isSampleResult || !sizeIsSyntheticLeftover ? lawnSize.trim() || undefined : undefined
  // Size that may travel outward (share/related links) - real, non-leftover.
  const outboundSize =
    !isSampleResult && !sizeIsSyntheticLeftover ? lawnSize.trim() || undefined : undefined

  // `mounted` keeps the time-dependent result out of server-rendered HTML.
  const canCalculate = mounted && isValidZip(zip) && !!grassType && !!thatchDepth

  // Live calculation: complete the inputs and the verdict appears/updates
  // immediately - no submit button. Pure derivation via useMemo.
  const { result, error } = useMemo((): {
    result: DethatchingResult | null
    error: string | null
  } => {
    if (!canCalculate || !thatchDepth) return { result: null, error: null }
    try {
      return {
        result: calculateDethatching({
          grassType,
          zipCode: zip.trim(),
          thatchDepth,
          lawnSize: sizeForEngine,
        }),
        error: null,
      }
    } catch (err) {
      return {
        result: null,
        error: err instanceof Error ? err.message : "Something went wrong. Try a nearby ZIP.",
      }
    }
  }, [canCalculate, grassType, zip, thatchDepth, sizeForEngine])

  // Analytics: signature-guarded (Set) so toggling back to a previous combo
  // does not re-fire. Sample verdicts are tagged and never count as usage or
  // trigger the first-result scroll.
  useEffect(() => {
    if (!result) return
    const signature = `${result.grassType}|${result.zipCode}|${result.thatchDepth}|${result.verdict}|${isSampleResult ? "s" : "r"}`
    if (trackedSignaturesRef.current.has(signature)) return
    trackedSignaturesRef.current.add(signature)

    if (!isSampleResult && !trackedUsageRef.current) {
      trackedUsageRef.current = true
      trackUsage("dethatching-calculator")
      // Standard funnel event (calculator_used). The bespoke
      // dethatching_calculated below stays for historical continuity.
      analytics.calculatorUsed("dethatching", {
        inputs: {
          grass_type: result.grassType,
          zip_code: result.zipCode,
          thatch_depth: result.thatchDepth,
          lawn_size_sqft: result.lawnSizeSqft ?? undefined,
        },
        result: {
          verdict: result.verdict,
          method: result.method,
          region: result.region,
          status: result.status,
        },
        source: "live_update",
      })
      setTimeout(() => {
        const el = document.getElementById("dethatching-result")
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 90
          window.scrollTo({ top: y, behavior: "smooth" })
        }
      }, 120)
    }
    trackEvent("dethatching_calculated", {
      grass_type: result.grassType,
      region: result.region,
      verdict: result.verdict,
      method: result.method,
      thatch_depth: result.thatchDepth,
      status: result.status,
      is_sample: isSampleResult,
    })
  }, [result, trackUsage, isSampleResult])

  const verdictTone = result ? VERDICT_TONE[result.verdict] : null
  const windowLabel = result
    ? `${fmtShort(result.recommendedWindow.start)} - ${fmtShort(result.recommendedWindow.end)}`
    : "Add your ZIP"

  return (
    <ToolPageShell
      breadcrumbLabel="Dethatching Calculator"
      after={
        <NewsletterSignupModal
          isOpen={showNewsletterModal}
          onClose={closeNewsletterModal}
          toolName="Dethatching Calculator"
          usageCount={usageCount}
          grassType={isSampleResult ? undefined : (result?.grassType ?? grassType)}
          zipCode={!isSampleResult && result ? result.zipCode : undefined}
        />
      }
    >
      <HeadlineHero
        eyebrow="Timed to your grass"
        eyebrowIcon={<CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />}
        title="Do you need to"
        accent="dethatch?"
        subtitle="Answer two quick questions to get a verdict, the right tool for the job, and the best weeks to dethatch in your area."
      />

      {/* Fast answer callout - the site's canonical .fast-answer treatment
          (frosted glass card, same as blog posts), not a bespoke box. */}
      <div className="fast-answer mb-6 max-w-3xl">
        <strong>Fast Answer</strong>
        <p className="m-0 text-sm">
          Thatch is the brown, spongy layer of dead stems and roots between the green blades and
          the soil. Under 1/2 inch it is healthy and protective. Past 1/2 inch it blocks water and
          fertilizer, and it is time to dethatch.
        </p>
      </div>

      <LawnPrefillBanner
        lawns={lawns}
        selectedLawnId={selectedLawnId}
        onSelect={setSelectedLawnId}
      />

      {/* Input card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-elevation-2 sm:p-6">
        <div className="flex flex-wrap gap-7">
          {/* Column 1: lawn facts */}
          <div className="flex min-w-0 flex-1 basis-[260px] flex-col gap-4">
            <div className="flex items-center gap-2">
              <StepChip n={1} />
              <span className="text-sm font-bold text-gray-900">Tell us about your lawn</span>
            </div>
            <div>
              <label
                htmlFor="dethatch-grass"
                className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-green-700"
              >
                <Sprout className="h-3.5 w-3.5" aria-hidden="true" />
                Grass type
              </label>
              <select
                id="dethatch-grass"
                value={grassType}
                onChange={(e) => {
                  setGrassType(e.target.value)
                  markTouched("grass")
                  poke()
                }}
                className="w-full min-h-[44px] rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/30"
              >
                <option value="">Select your grass</option>
                {GRASS_OPTIONS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="dethatch-zip"
                className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-green-700"
              >
                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                ZIP code
              </label>
              <ZipCodeInput
                id="dethatch-zip"
                value={zip}
                onChange={(z) => {
                  setZip(z)
                  markTouched("zip")
                  poke()
                }}
                showLabel={false}
                placeholder="e.g. 43004"
                rememberLastZip={!isSampleResult}
              />
              {result && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-xs font-semibold text-green-800">
                  <MapPin className="h-3 w-3" aria-hidden="true" />
                  {isSampleResult ? "Sample lawn · " : ""}
                  {result.region}
                  {result.regionalData.firstFrost
                    ? ` · first frost about ${fmtShort(result.regionalData.firstFrost)}`
                    : ""}
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="dethatch-size"
                className="mb-2 inline-flex items-baseline gap-1.5 text-xs font-bold uppercase tracking-wider text-green-700"
              >
                <Ruler className="h-3.5 w-3.5 self-center" aria-hidden="true" />
                Lawn size <span className="font-normal normal-case tracking-normal text-gray-400">(optional, for equipment sizing)</span>
              </label>
              <input
                id="dethatch-size"
                inputMode="numeric"
                placeholder="sq ft"
                value={lawnSize}
                onChange={(e) => {
                  setLawnSize(e.target.value.replace(/[^\d]/g, ""))
                  markTouched("size")
                  poke()
                }}
                className="w-full min-h-[44px] rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/30"
              />
            </div>
          </div>

          {/* Column 2: tappable cross-section */}
          <div className="min-w-0 flex-[1.3] basis-[320px]">
            <div className="mb-3 flex items-center gap-2">
              <StepChip n={2} />
              <span className="text-sm font-bold text-gray-900">Tap the picture that looks like your lawn</span>
            </div>
            <div className="mb-2 text-xs text-gray-500">
              Each picture shows a side view of turf. The brown band is the thatch layer.
            </div>
            <ThatchBandSelector
              value={thatchDepth}
              onChange={(b) => {
                setThatchDepth(b)
                markTouched("thatch")
                poke()
              }}
            />
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-800">{error}</p>
          </div>
        )}
      </div>

      {/* Verdict */}
      {result && verdictTone && (
        <div id="dethatching-result" className="mt-8 space-y-6">
          {/* Peak-moment frame with the single primary next action. */}
          <ResultSuccessCard
            eyebrow={verdictTone.eyebrow}
            stats={
              result.needsDethatching
                ? [
                    { icon: Layers, label: "Thatch", value: result.thatchInches },
                    { icon: Wrench, label: "Method", value: result.methodLabel.replace(/\s*\(.*\)$/, "") },
                    { icon: CalendarClock, label: "Window", value: windowLabel },
                  ]
                : [{ icon: Layers, label: "Thatch", value: result.thatchInches }]
            }
            interpretation={
              result.needsDethatching ? (
                <>
                  Dethatch your {result.grassType} lawn with a {result.methodLabel.toLowerCase()} during{" "}
                  {formatDateRange(result.recommendedWindow.start, result.recommendedWindow.end)}, then
                  overseed while the soil is exposed. Confirm soil temperature first so you hit the growth window.
                </>
              ) : (
                <>
                  Your {result.grassType} lawn does not need dethatching at {result.thatchInches}. Keep that
                  layer in check with a core-aeration pass in your window instead.
                </>
              )
            }
            cta={{
              href: buildRelatedToolUrl(
                result.needsDethatching ? "soil-temperature" : "aeration-timing-tool",
                "dethatching-calculator",
                // Never carry sample values into another tool as if real.
                isSampleResult ? {} : { zip: result.zipCode }
              ),
              label: result.needsDethatching ? "Check soil temperature" : "Plan your aeration",
              onClick: () =>
                trackEvent("cta_clicked", {
                  cta_location: "dethatching_result_next_step",
                  cta_text: result.needsDethatching ? "Check soil temperature" : "Plan your aeration",
                  is_sample: isSampleResult,
                }),
            }}
            share={{
              // Carry grass, ZIP, thatch band, and real size so the recipient
              // reopens the SAME verdict. Suppressed entirely for a sample.
              url: buildToolUrl(
                "dethatching-calculator",
                isSampleResult
                  ? {}
                  : {
                      grassType: result.grassType,
                      zip: result.zipCode,
                      thatchBand: result.thatchDepth,
                      lawnSize: outboundSize,
                    }
              ),
              title: "Do you need to dethatch?",
              text: isSampleResult
                ? "Do you need to dethatch? Get your verdict, the right tool, and your timing."
                : `Dethatching plan for my ${result.grassType} lawn: ${result.verdictHeadline}`,
              trackingId: "dethatching-calculator",
            }}
          />

          <ResultEmailCaptureCard
            source="tool_result_dethatching"
            toolDisplayName="dethatching plan"
            subcopy={`We'll remind you when your ${result.region} dethatching window opens.`}
            grassType={isSampleResult ? undefined : result.grassType}
            zipCode={!isSampleResult && isValidZip(result.zipCode) ? result.zipCode : undefined}
            accountLead={{
              redirectTo: buildToolUrl(
                "dethatching-calculator",
                isSampleResult
                  ? {}
                  : {
                      grassType: result.grassType,
                      zip: result.zipCode,
                      thatchBand: result.thatchDepth,
                      lawnSize: outboundSize,
                    },
              ),
              headline: isSampleResult
                ? "Save my dethatching plan"
                : `Save my dethatching plan for ${result.grassType}`,
              subcopy: "Keep this dethatching plan in a free account and track your lawn over time.",
              dividerLabel: isSampleResult ? undefined : "or get your 12-month calendar by email",
              onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_dethatching"),
            }}
            successSubcopy={isSampleResult ? "Your lawn tips are on the way. Your dethatching plan stays right here to revisit anytime." : undefined}
            onCaptured={() => closeNewsletterModal()}
          />

          <div
            className={`rounded-2xl border-[1.5px] bg-white p-6 shadow-elevation-2 transition-[opacity,border-color] duration-300 sm:p-7 ${verdictTone.border}`}
            style={{ opacity: revealing ? 0.35 : 1 }}
            aria-live="polite"
          >
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <StepChip n={3} dark />
              <span className="text-sm font-bold text-gray-900">Your verdict</span>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <div className="mx-auto min-w-[260px] flex-[0_1_320px]">
                <VerdictCrossSection
                  verdict={result.verdict}
                  band={result.thatchDepth}
                  thatchInches={result.thatchInches}
                />
              </div>
              <div className="min-w-0 flex-1 basis-[300px]">
                <div className="mb-2.5 flex flex-wrap items-center gap-2.5">
                  <span className={`rounded-full px-3 py-1 text-xs font-bold transition-colors duration-300 ${verdictTone.badge}`}>
                    {verdictTone.eyebrow}
                  </span>
                  <span className="text-xs text-gray-500">
                    {result.grassType} · {result.region} · thatch {result.thatchInches}
                  </span>
                </div>
                <h2 className="text-[22px] font-extrabold leading-tight tracking-tight text-gray-900 sm:text-[26px]">
                  {result.verdictHeadline}
                </h2>
                <p className="mt-2.5 text-[15px] leading-relaxed text-gray-700">{result.verdictSummary}</p>
              </div>
            </div>

            <MonthWindowStrip result={result} />
            <MethodTiles result={result} windowLabel={windowLabel} />

            <div className="mt-5 space-y-2.5 text-sm leading-relaxed text-gray-700">
              <p>
                <span className="font-bold text-gray-900">Why this method: </span>
                {result.methodReason}
              </p>
              <p>
                <span className="font-bold text-gray-900">Equipment: </span>
                {result.equipmentGuidance}
              </p>
              <p>
                <span className="font-bold text-gray-900">Timing: </span>
                {result.timingAdvice}
                {result.timeUntilOptimal ? ` (${result.timeUntilOptimal}).` : ""}
              </p>
            </div>
          </div>

          {/* Step-by-step */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-elevation-2 sm:p-6">
            <h3 className="mb-3 text-base font-semibold text-gray-900">
              {result.needsDethatching ? "How to dethatch, step by step" : "Your maintenance plan"}
            </h3>
            <ol className="space-y-2.5">
              {result.steps.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm leading-relaxed text-gray-700">
                  <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-green-100 text-xs font-extrabold text-green-700">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <div className="mt-4 border-t border-gray-100 pt-4">
              <h4 className="mb-2.5 text-[11px] font-extrabold uppercase tracking-wider text-gray-500">
                Avoid these
              </h4>
              <ul className="space-y-2">
                {result.avoidActions.map((a, i) => (
                  <li key={i} className="flex gap-2.5 text-[13px] leading-normal text-gray-600">
                    <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" aria-hidden="true" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Post-result affiliate picks - the buying moment for a dethatcher or
              power rake. Suppressed for the monitor verdict (no machine needed). */}
          {result.needsDethatching && (
            <ProductRecommendations
              categories={["Lawn Mowers & Maintenance Tools"]}
              keywords={["dethatcher", "power rake", "verticutter", "thatch rake", "dethatching"]}
              title="Dethatching gear to have ready"
              description="Power rakes, tow-behind dethatchers, and thatch rakes for the job."
              trackingContext="dethatching-calculator"
              compact
            />
          )}
        </div>
      )}

      <RelatedTools
        className="mt-10"
        currentToolId="dethatching-calculator"
        prefillValues={
          isSampleResult
            ? {}
            : {
                zip: result?.zipCode ?? (isValidZip(zip) ? zip : lawnPrefill.zipCode),
                grassType: result?.grassType ?? grassType ?? lawnPrefill.grassType,
                lawnSize: outboundSize ?? lawnPrefill.lawnSizeSqft,
              }
        }
      />
    </ToolPageShell>
  )
}

export function DethatchingCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="dethatching-calculator" />}>
      <SearchParamsHydrationGate dateSensitive>
        <DethatchingContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
